import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, RotateCcw } from 'lucide-react';
import { Modale } from './ui/Modale';
import CalendrierAdmin from './CalendrierAdmin';
import { BoutonMint, BoutonSecondaire } from './ui/Primitives';
import { useToast } from './ui/useToast';
import { appelApi, ErreurApi } from '../lib/api';
import { JourCalendrierLocal, TypeJour } from '../lib/calendrier-utils';
import {
  dateFr,
  ecartCycle,
  finProposee,
  libelleDuree,
  libelleRangAnnee,
} from '../lib/cycle-formation';
import { attributsOnglet, attributsPanneau, clavierOnglets } from './ui/onglets-aria';

interface ModaleCalendrierProps {
  onFermer: () => void;
  formationId: string;
  titre?: string;
}

/** Réponse de `GET /api/formations/:id/programmation`. */
interface Programmation {
  dureeAnnees: number | null;
  sessions: { id: string; libelle: string; site: string; dateDebut: string; dateFin: string }[];
  programme: { premier: string | null; dernier: string | null; total: number };
  jours: { date: string; type: TypeJour }[];
  horsPeriode: { avant: number; apres: number };
}

/** Date civile `AAAA-MM-JJ` ↔ minuit local : la grille raisonne en jours, pas en instants. */
function versLocal(iso: string): Date {
  const [annee, mois, jour] = iso.split('-').map(Number);
  return new Date(annee!, mois! - 1, jour!);
}
function versIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Années scolaires (septembre → août) que couvre la période : ce sont les pages
 * du calendrier, à l'écran comme dans le PDF du dossier.
 */
function anneesScolaires(du: string, au: string): { rang: number; anneeDebut: number; du: string; au: string }[] {
  const scolaire = (iso: string) => (Number(iso.slice(5, 7)) >= 9 ? Number(iso.slice(0, 4)) : Number(iso.slice(0, 4)) - 1);
  const liste = [];
  for (let annee = scolaire(du), rang = 1; annee <= scolaire(au); annee += 1, rang += 1) {
    const debut = `${annee}-09-01`;
    const fin = `${annee + 1}-08-31`;
    liste.push({ rang, anneeDebut: annee, du: debut < du ? du : debut, au: fin > au ? au : fin });
  }
  return liste;
}

/** Rentrée de septembre de l'année scolaire en cours. */
function rentreeCourante(): string {
  const aujourdhui = new Date();
  const annee = aujourdhui.getMonth() >= 8 ? aujourdhui.getFullYear() : aujourdhui.getFullYear() - 1;
  return `${annee}-09-01`;
}

/**
 * Tous les jours de la période, ceux déjà programmés gardant leur affectation.
 * Un jour jamais programmé part en « Entreprise », comme la grille l'a toujours
 * proposé.
 */
function joursDeLaPeriode(du: string, au: string, programmes: Programmation['jours']): JourCalendrierLocal[] {
  const connus = new Map(programmes.map((jour) => [jour.date, jour.type]));
  const jours: JourCalendrierLocal[] = [];
  const fin = versLocal(au);
  for (const courant = versLocal(du); courant <= fin; courant.setDate(courant.getDate() + 1)) {
    jours.push({ date: new Date(courant), type: connus.get(versIso(courant)) ?? 'ENTREPRISE' });
  }
  return jours;
}

/**
 * Programmation du calendrier d'une formation, sur **toute** sa durée.
 *
 * La période vient d'une session — ses dates réelles — ou d'une rentrée saisie,
 * la fin étant alors proposée par la durée de la formation. Le calendrier se
 * parcourt année par année (1ʳᵉ, 2ᵉ, 3ᵉ…) et s'enregistre d'un seul geste.
 * Seuls les jours de la période sont réécrits : ce qui a été programmé avant
 * ou après est conservé, et l'écran le dit.
 */
export function ModaleCalendrier({
  onFermer,
  formationId,
  titre = 'Programmation du calendrier',
}: ModaleCalendrierProps) {
  const toast = useToast();
  const [programmation, setProgrammation] = useState<Programmation | null>(null);
  const [source, setSource] = useState<string>('');
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [finTouchee, setFinTouchee] = useState(false);
  const [jours, setJours] = useState<JourCalendrierLocal[]>([]);
  const [modifie, setModifie] = useState(false);
  const [rang, setRang] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [sauvegardeEnCours, setSauvegardeEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const url = `/formations/${encodeURIComponent(formationId)}/programmation`;
  const duree = programmation?.dureeAnnees ?? null;

  // Premier chargement : sessions, durée, et période proposée.
  useEffect(() => {
    let actif = true;
    void appelApi<Programmation>(url)
      .then((etat) => {
        if (!actif) return;
        setProgrammation(etat);
        // Par défaut, le cycle complet de la formation : de la rentrée déjà
        // programmée (ou de la rentrée en cours) jusqu'au bout de sa durée. Une
        // session d'un an ne raccourcit plus d'office la vue d'une formation de
        // trois ans : elle reste au choix dans la liste.
        const premier = etat.programme.premier;
        const debut = premier
          ? `${Number(premier.slice(5, 7)) >= 9 ? premier.slice(0, 4) : Number(premier.slice(0, 4)) - 1}-09-01`
          : rentreeCourante();
        setSource('rentree');
        setDu(debut);
        setAu(finProposee(debut, etat.dureeAnnees ?? 1) ?? debut);
      })
      .catch(() => actif && setErreur('La programmation n’a pas pu être chargée.'));
    return () => {
      actif = false;
    };
  }, [url]);

  const periodeValide = /^\d{4}-\d{2}-\d{2}$/.test(du) && /^\d{4}-\d{2}-\d{2}$/.test(au) && au >= du;
  const annees = useMemo(() => (periodeValide ? anneesScolaires(du, au) : []), [du, au, periodeValide]);
  const [exportEnCours, setExportEnCours] = useState(false);

  /** PDF de toute la période, composé par le serveur au gabarit du dossier. */
  const exporter = async (): Promise<void> => {
    if (!periodeValide) return;
    if (modifie) {
      toast('Enregistrez le calendrier avant de l’exporter : le PDF reprend ce qui est en base.');
      return;
    }
    setExportEnCours(true);
    try {
      const reponse = await fetch(`${import.meta.env.VITE_API_BASE ?? '/api'}${url}/pdf?du=${du}&au=${au}`, {
        credentials: 'include',
      });
      if (!reponse.ok) throw new Error();
      const lien = document.createElement('a');
      lien.href = URL.createObjectURL(await reponse.blob());
      lien.download = `calendrier-${du}-${au}.pdf`;
      lien.click();
      URL.revokeObjectURL(lien.href);
    } catch {
      toast('Le PDF n’a pas pu être généré. Réessayez dans quelques instants.');
    } finally {
      setExportEnCours(false);
    }
  };

  // Les jours de la période, rechargés quand elle change.
  const chargerJours = useCallback(async () => {
    if (!periodeValide) return;
    setChargement(true);
    try {
      const etat = await appelApi<Programmation>(`${url}?du=${du}&au=${au}`);
      setProgrammation(etat);
      setJours(joursDeLaPeriode(du, au, etat.jours));
      setModifie(false);
      setErreur(null);
    } catch {
      setErreur('Le calendrier existant n’a pas pu être chargé.');
    } finally {
      setChargement(false);
    }
  }, [url, du, au, periodeValide]);

  useEffect(() => {
    void chargerJours();
  }, [chargerJours]);

  useEffect(() => {
    if (rang > annees.length) setRang(Math.max(1, annees.length));
  }, [annees.length, rang]);

  const choisirSource = (valeur: string): void => {
    setSource(valeur);
    setFinTouchee(false);
    const session = programmation?.sessions.find((s) => `session:${s.id}` === valeur);
    if (session) {
      setDu(session.dateDebut);
      setAu(session.dateFin);
    } else {
      const debut = du || rentreeCourante();
      setDu(debut);
      setAu(finProposee(debut, duree ?? 1) ?? debut);
    }
  };

  const changerDebut = (valeur: string): void => {
    setDu(valeur);
    // La fin suit la durée tant qu'elle n'a pas été corrigée à la main.
    if (!finTouchee) {
      const proposee = finProposee(valeur, duree ?? 1);
      if (proposee) setAu(proposee);
    }
  };

  const modifierJours: React.Dispatch<React.SetStateAction<JourCalendrierLocal[]>> = (action) => {
    setModifie(true);
    setJours(action);
  };

  const handleEnregistrer = async () => {
    if (!periodeValide) return;
    setSauvegardeEnCours(true);
    try {
      const etat = await appelApi<Programmation>(url, {
        methode: 'PUT',
        corps: { du, au, jours: jours.map((jour) => ({ date: versIso(jour.date), type: jour.type })) },
      });
      setProgrammation(etat);
      setModifie(false);
      toast(`Calendrier enregistré du ${dateFr(du)} au ${dateFr(au)}`);
    } catch (e) {
      toast(e instanceof ErreurApi ? e.message : 'Erreur lors de la sauvegarde du calendrier');
    } finally {
      setSauvegardeEnCours(false);
    }
  };

  const anneeVisible = annees[rang - 1] ?? null;
  const ecart = periodeValide ? ecartCycle(duree, du, au) : null;
  const sessionRetenue = programmation?.sessions.find((s) => `session:${s.id}` === source) ?? null;
  const horsPeriode = programmation?.horsPeriode ?? { avant: 0, apres: 0 };
  const champ =
    'min-h-[40px] w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy disabled:bg-gray-50 disabled:text-codialis-gray';

  return (
    <Modale
      titre={titre}
      sousTitre="Configurez les jours de centre de formation, d'entreprise, les congés et jours fériés, sur toute la durée de la formation."
      ouverte={true}
      onFermer={onFermer}
      largeur="max-w-6xl"
    >
      <div className="space-y-4 p-4 sm:p-6">
        {/* Période programmée */}
        <section className="rounded-2xl border border-gray-100 bg-codialis-sand/40 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="min-w-0 text-xs font-bold uppercase tracking-wide text-codialis-navy">
              Période
              <select
                value={source}
                disabled={modifie}
                onChange={(evenement) => choisirSource(evenement.target.value)}
                className={`mt-1 ${champ}`}
              >
                {(programmation?.sessions ?? []).map((session) => (
                  <option key={session.id} value={`session:${session.id}`}>
                    {session.libelle} · {session.site}
                  </option>
                ))}
                <option value="rentree">Cycle complet de la formation</option>
              </select>
            </label>
            <label className="min-w-0 text-xs font-bold uppercase tracking-wide text-codialis-navy">
              Début de formation
              <input
                type="date"
                value={du}
                disabled={modifie || sessionRetenue !== null}
                onChange={(evenement) => changerDebut(evenement.target.value)}
                className={`mt-1 ${champ}`}
              />
            </label>
            <label className="min-w-0 text-xs font-bold uppercase tracking-wide text-codialis-navy">
              Fin de formation
              <div className="mt-1 flex gap-2">
                <input
                  type="date"
                  value={au}
                  min={du || undefined}
                  disabled={modifie || sessionRetenue !== null}
                  onChange={(evenement) => {
                    setFinTouchee(true);
                    setAu(evenement.target.value);
                  }}
                  className={`${champ} min-w-0 flex-1`}
                />
                {finTouchee && sessionRetenue === null && !modifie ? (
                  <button
                    type="button"
                    onClick={() => {
                      setFinTouchee(false);
                      const proposee = finProposee(du, duree ?? 1);
                      if (proposee) setAu(proposee);
                    }}
                    className="grid min-h-[40px] min-w-[40px] place-items-center rounded-xl border border-gray-200 bg-white text-codialis-gray hover:text-codialis-navy"
                    aria-label="Revenir à la fin proposée par la durée"
                    title="Revenir à la fin proposée par la durée"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                  </button>
                ) : null}
              </div>
            </label>
          </div>

          {periodeValide ? (
            <p className="mt-3 text-sm text-codialis-navy" aria-live="polite">
              <span className="font-semibold">
                Durée : {duree ? libelleDuree(duree) : 'non renseignée'}
              </span>{' '}
              · Du {dateFr(du)} au {dateFr(au)} · {annees.length}{' '}
              {annees.length > 1 ? 'années scolaires' : 'année scolaire'}
            </p>
          ) : (
            <p className="mt-3 text-sm font-semibold text-red-700">
              La fin de formation ne peut pas précéder son début.
            </p>
          )}
          {sessionRetenue ? (
            <p className="mt-1 text-xs text-codialis-gray">
              Dates réelles de la session, modifiables depuis l’écran des sessions.
            </p>
          ) : duree === null ? (
            <p className="mt-1 text-xs text-codialis-gray">
              Renseignez la durée de la formation pour que la fin soit proposée sur tout le cycle.
            </p>
          ) : null}
          {ecart ? (
            <p className="mt-2 flex items-start gap-2 text-xs font-semibold text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {ecart}
            </p>
          ) : null}
          {horsPeriode.avant + horsPeriode.apres > 0 ? (
            <p className="mt-2 flex items-start gap-2 text-xs text-codialis-gray">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {horsPeriode.avant + horsPeriode.apres} jour(s) programmé(s) hors de cette période
              {horsPeriode.avant > 0 ? ` (${horsPeriode.avant} avant` : ' ('}
              {horsPeriode.avant > 0 && horsPeriode.apres > 0 ? ', ' : ''}
              {horsPeriode.apres > 0 ? `${horsPeriode.apres} après` : ''}) : ils sont conservés,
              l’enregistrement ne touche que la période affichée.
            </p>
          ) : null}
          {modifie ? (
            <p className="mt-2 text-xs text-codialis-gray">
              Modifications en cours : enregistrez-les, ou{' '}
              <button type="button" className="font-semibold text-codialis-blue underline" onClick={() => void chargerJours()}>
                annulez-les
              </button>
              , avant de changer de période.
            </p>
          ) : null}
        </section>

        {/* Navigation par année de formation */}
        {/* Toujours affichée, même sur une seule année : c'est le repère de
            ce qui est programmé, et elle ne doit pas disparaître en cours de route. */}
        {annees.length > 0 ? (
          <div
            className="flex flex-wrap gap-2"
            role="tablist"
            aria-label="Années de formation"
            onKeyDown={clavierOnglets(
              annees.map((annee) => String(annee.rang)),
              String(rang),
              (cle) => setRang(Number(cle)),
            )}
          >
            {annees.map((annee) => (
              <button
                key={annee.rang}
                type="button"
                {...attributsOnglet(String(annee.rang), annee.rang === rang, 'panneau-annee-calendrier')}
                onClick={() => setRang(annee.rang)}
                className={`min-h-[40px] rounded-xl border px-4 text-xs font-semibold transition ${
                  annee.rang === rang
                    ? 'border-codialis-mint bg-codialis-mint text-codialis-navy'
                    : 'border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
                }`}
              >
                {libelleRangAnnee(annee.rang)}
                <span className="ml-1 font-normal">
                  · {annee.anneeDebut}-{annee.anneeDebut + 1}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {erreur ? <p className="text-sm text-red-700" role="alert">{erreur}</p> : null}

        <div {...(annees.length > 0 ? attributsPanneau('panneau-annee-calendrier', String(rang)) : {})} className="space-y-4">
          {chargement && jours.length === 0 ? (
            <p className="text-sm text-codialis-gray">Chargement du calendrier…</p>
          ) : anneeVisible ? (
            <>
              <p className="text-sm font-semibold text-codialis-navy">
                {libelleRangAnnee(anneeVisible.rang)} · du {dateFr(anneeVisible.du)} au {dateFr(anneeVisible.au)}
              </p>
              <CalendrierAdmin
                jours={jours}
                setJours={modifierJours}
                anneeDebut={anneeVisible.anneeDebut}
                onExporter={() => void exporter()}
                exportEnCours={exportEnCours}
              />
            </>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4">
          <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
          <BoutonMint onClick={handleEnregistrer} disabled={sauvegardeEnCours || !periodeValide || chargement}>
            {sauvegardeEnCours ? 'Sauvegarde...' : 'Enregistrer le calendrier'}
          </BoutonMint>
        </div>
      </div>
    </Modale>
  );
}
